import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const CUSTOM_IMAGE_THUMB_STYLE_ID = 'cart-custom-image-thumbnail-style';
const CUSTOM_PRODUCT_INP_STYLE_ID = 'custom-product-inp-style';

function ensureCustomImageThumbnailStyles() {
  if (document.getElementById(CUSTOM_IMAGE_THUMB_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = CUSTOM_IMAGE_THUMB_STYLE_ID;
  style.textContent = `
    cart-drawer-component .cart-items__properties {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
    }

    cart-drawer-component .cart-items__properties img {
      width: auto !important;
      height: auto !important;
      max-width: 80px;
      max-height: 100px;
      object-fit: contain;
    }
  `;

  document.head.appendChild(style);
}

function ensureCustomProductInpStyles() {
  if (document.getElementById(CUSTOM_PRODUCT_INP_STYLE_ID)) return;
  if (!window.location.pathname.includes('/products/custom-paint-by-numbers-kit')) return;

  const style = document.createElement('style');
  style.id = CUSTOM_PRODUCT_INP_STYLE_ID;
  style.textContent = `
    @supports (content-visibility: auto) {
      #shopify-section-template--22508434686208__customer_reviews_rLF6zt,
      #shopify-section-template--22508434686208__section_H3yqgF,
      #shopify-section-template--22508434686208__section_kBYDRf,
      #shopify-section-template--22508434686208__section_mfwU4P,
      #shopify-section-template--22508434686208__section_i77Ymi,
      #shopify-section-template--22508434686208__section_Wt9XmK,
      #shopify-section-template--22508434686208__product_recommendations_qggXJq,
      #shopify-section-template--22508434686208__product_list_EQ66EQ,
      #shopify-section-template--22508434686208__product_list_K34gCB,
      #shopify-section-template--22508434686208__section_eeENRF {
        content-visibility: auto;
        contain-intrinsic-size: auto 650px;
      }

      #shopify-section-template--22508434686208__1773146859b033079b {
        content-visibility: auto;
        contain-intrinsic-size: auto 2600px;
      }
    }
  `;

  document.head.appendChild(style);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    ensureCustomImageThumbnailStyles();
    ensureCustomProductInpStyles();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
